import * as React from 'react';
import styled from 'styled-components';
import FieldDecorator from './FieldDecorator';
import { InputProps } from './Input';
import Option from '../../models/Option';
import { selectedBlue } from '../../appComponents/cssConstants';

const MultipleChoiceField = (props: Props) => {
	const {
		onValueChange,
		errorMessage,
		label,
		name,
		fieldId,
		options,
		required,
		value,
		validateOnMount = true,
		type,
		...rest
	} = props;
	const [errorMessageState, setErrorMessageState] = React.useState('');

	React.useEffect(() => {
		if (validateOnMount) {
			// In order to allow a parent to know whether the initial state of an input is valid or not, we fire the
			// onValueChange code.
			const notSet = props.value === undefined || props.value === null || props.value === '';

			// TODO This code block is repeated from Input.tsx, consider revising.
			let errorMessageLabel = props.label;
			if (label.length > 30 || label.includes('?') || props.label.includes('[')) errorMessageLabel = 'This';

			const updatedErrorMessage = required && notSet ? `${errorMessageLabel} is required.` : null;

			onValueChange(name, props.value, updatedErrorMessage);
		}
	}, []);

	const optionValues = options.map((x) => x.value);
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { target } = e;
		const toggledValue = e.target.id.replace(name, '');

		let newErrorMessage: string = null;
		let newValue: string | string[] = target.value;
		if (type === 'checkbox') {
			const originalValue: string[] = (value as string[]) || [];
			const stillSelected = originalValue.filter((x) => x !== toggledValue);
			const optionStillSelectedOrNowOn = (x: string) =>
				stillSelected.includes(x) || (x === toggledValue && target.checked);
			newValue = optionValues.filter(optionStillSelectedOrNowOn);
		}

		if (props.required && (newValue === null || newValue.length === 0)) {
			newErrorMessage = `${type === 'radio' ? 'One' : 'At least one'} item must be selected.`;
		}
		setErrorMessageState(newErrorMessage);

		onValueChange(name, newValue, newErrorMessage);
	};

	return (
		<FieldDecorator
			className={props.className}
			label={label}
			errorMessage={errorMessage || errorMessageState}
			id={fieldId}
		>
			{options.map((o) => {
				const checked = o.value === value || (type === 'checkbox' && value !== null && value?.includes(o.value));
				return (
					<div key={o.value}>
						<StyledInput
							type={type}
							className={`k-${type}`}
							id={`${name}${o.value}`}
							value={type === 'radio' ? o.value : undefined}
							onChange={handleChange}
							name={name}
							checked={checked}
							disabled={false}
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...rest}
						/>
						<Label htmlFor={`${name}${o.value}`}>{o.text}</Label>
					</div>
				);
			})}
		</FieldDecorator>
	);
};

interface Props extends InputProps {
	name: string;
	onValueChange: (
		name: string,
		value: string | number | boolean | string[] | null | Option<string>,
		errorMessage: string
	) => void;
	options: Option<string>[];
	value: string | string[];
	type: 'checkbox' | 'radio';
	fieldId?: string;
	validateOnMount?: boolean;
	label?: string;
	errorMessage?: string;
}

MultipleChoiceField.defaultProps = {
	fieldId: null,
	validateOnMount: true,
	label: null,
	errorMessage: null
};

export default MultipleChoiceField;

const Label = styled.label`
	margin-inline-start: 8px;
	font-weight: normal !important; /* This is just to overwrite a DataCapturePage CSS rule. */
`;

const StyledInput = styled.input<{ type: string }>`
	&:focus {
		/* This hides an orange border that is rendered on selected radio buttons and checkboxes. */
		display: none;
	}
	${(props) =>
		props.type === 'radio' ? `&:checked { border-color: ${selectedBlue}; background-color: ${selectedBlue}; }` : ''}
`;
